import { useState, useRef } from "react";

const MEMBER_COLORS = [
  "#4e9af1","#f1c04e","#4ef18a","#c04ef1","#4ef1e8",
  "#f18a4e","#8af14e","#f14ec0","#8a4ef1","#e8f14e",
  "#4ef1c0","#f1884e","#a0d4ff","#ffe0a0","#a0ffcc","#e0a0ff",
];
const memberColor = (id) => MEMBER_COLORS[(id - 1) % MEMBER_COLORS.length];

const MEMBER_NAMES = [
  "Main Hoop","Front Hoop","Hoop Brace Left","Hoop Brace Right",
  "A-Pillar Left","A-Pillar Right","B-Pillar Left","B-Pillar Right",
  "Roof Rail Left","Roof Rail Right","Rocker Left","Rocker Right",
  "Front Rail Left","Front Rail Right","Rear Rail Left","Rear Rail Right",
  "Floor Crossmember Front","Floor Crossmember Mid","Floor Crossmember Rear",
  "Dash Bar","Firewall Brace Left","Firewall Brace Right",
  "Transmission Tunnel Left","Transmission Tunnel Right",
  "Harness Bar","Intrusion Bar Left","Intrusion Bar Right",
  "Door Diagonal Left","Door Diagonal Right","Windshield Bar",
  "Rear Brace Left","Rear Brace Right","Rear Crossmember","Trunk Bar",
  "Diff Hoop Left","Diff Hoop Right",
  "Shock Mount Front Left","Shock Mount Front Right",
  "Shock Mount Rear Left","Shock Mount Rear Right",
  "Control Arm Brace Left","Control Arm Brace Right",
  "Lower Wishbone Front Left","Lower Wishbone Front Right",
  "Upper Wishbone Front Left","Upper Wishbone Front Right",
  "Lower Wishbone Rear Left","Lower Wishbone Rear Right",
  "Upper Wishbone Rear Left","Upper Wishbone Rear Right",
  "Toe Link Left","Toe Link Right",
  "Push Rod Front Left","Push Rod Front Right",
  "Push Rod Rear Left","Push Rod Rear Right",
  "Anti-Roll Bar Front","Anti-Roll Bar Rear",
  "Steering Column Brace","Pedal Box Crossmember",
  "Battery Tray Rail Left","Battery Tray Rail Right",
  "Radiator Hoop Top","Radiator Hoop Bottom",
  "Nose Cone Strut Left","Nose Cone Strut Right",
  "Splitter Strut Left","Splitter Strut Right",
  "Wing Pillar Left","Wing Pillar Right","Wing Mount Crossbar",
  "Undertray Brace Front","Undertray Brace Rear",
  "Skid Plate Rail Left","Skid Plate Rail Right",
  "Engine Mount Left","Engine Mount Right","Gearbox Hanger",
  "Exhaust Hanger Front","Exhaust Hanger Rear",
  "Fuel Cell Brace Front","Fuel Cell Brace Rear",
  "Fuel Cell Side Rail Left","Fuel Cell Side Rail Right",
  "Accumulator Brace Top","Accumulator Brace Bottom",
  "Tractive System Rail Left","Tractive System Rail Right",
  "Front Bulkhead Top","Front Bulkhead Bottom",
  "Front Bulkhead Left","Front Bulkhead Right",
  "Main Hoop Brace Left","Main Hoop Brace Right",
  "Side Impact Member Left","Side Impact Member Right",
  "Diagonal Brace Left","Diagonal Brace Right",
  "Jacking Point Left","Jacking Point Right",
];

const toInches = (val, unit) => {
  const v = parseFloat(val);
  if (isNaN(v)) return NaN;
  if (unit === "ft") return v * 12;
  if (unit === "mm") return v / 25.4;
  if (unit === "cm") return v / 2.54;
  return v;
};
const fromInches = (val, unit) => {
  if (unit === "ft") return val / 12;
  if (unit === "mm") return val * 25.4;
  if (unit === "cm") return val * 2.54;
  return val;
};
const fmt = (n, dec = 3) => {
  const f = parseFloat(n);
  if (isNaN(f)) return "—";
  const s = f.toFixed(dec).replace(/\.?0+$/, "");
  return s === "-0" ? "0" : s;
};

function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  if (!text) return <>{children}</>;
  return (
    <div style={{ position: "relative", display: "contents" }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div style={{
          position: "fixed", background: "#1e1e1e", border: "1px solid #555",
          color: "#ccc", padding: "7px 11px", borderRadius: 4, fontSize: 12,
          zIndex: 9999, maxWidth: 260, pointerEvents: "none", lineHeight: 1.5,
          boxShadow: "0 4px 16px rgba(0,0,0,0.6)",
          transform: "translateY(-110%)",
        }}>
          {text}
        </div>
      )}
    </div>
  );
}

function TooltipWrap({ text, children }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  return (
    <div style={{ position: "relative", width: "100%" }}
      onMouseEnter={e => { setShow(true); setPos({ x: e.clientX, y: e.clientY }); }}
      onMouseMove={e => setPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setShow(false)}>
      {children}
      {show && text && (
        <div style={{
          position: "fixed", left: pos.x + 12, top: pos.y - 40,
          background: "#1e1e1e", border: "1px solid #555", color: "#ccc",
          padding: "6px 10px", borderRadius: 4, fontSize: 12,
          zIndex: 9999, maxWidth: 280, pointerEvents: "none", lineHeight: 1.5,
          boxShadow: "0 4px 16px rgba(0,0,0,0.6)", whiteSpace: "normal",
        }}>
          {text}
        </div>
      )}
    </div>
  );
}

function optimizeCuts(stockPiecesIn, allMembers, kerfIn) {
  const expanded = [];
  allMembers.forEach((m) => {
    const len = parseFloat(m.lenIn);
    const qty = Math.max(1, parseInt(m.qty) || 1);
    if (!isNaN(len) && len > 0) {
      for (let q = 0; q < qty; q++) {
        expanded.push({ len, id: m.id, name: m.name, subIdx: q, qty });
      }
    }
  });
  const sorted = [...expanded].sort((a, b) => b.len - a.len);
  const bins = stockPiecesIn.map((sp, i) => ({
    stockIdx: i, stockId: sp.id, stockLen: sp.lenIn,
    items: [], kerfUsed: 0, remaining: sp.lenIn,
  }));
  const unassigned = [];
  const maxStock = Math.max(...stockPiecesIn.map(s => s.lenIn));
  for (const item of sorted) {
    if (item.len > maxStock) { unassigned.push({ ...item, reason: "oversized" }); continue; }
    let placed = false;
    for (const bin of bins) {
      const needed = item.len + (bin.items.length > 0 ? kerfIn : 0);
      if (bin.remaining >= needed - 1e-9) {
        bin.kerfUsed += bin.items.length > 0 ? kerfIn : 0;
        bin.items.push(item);
        bin.remaining = +(bin.remaining - needed).toFixed(8);
        placed = true; break;
      }
    }
    if (!placed) unassigned.push({ ...item, reason: "no_space" });
  }
  return { bins: bins.filter(b => b.items.length > 0), unassigned };
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function generateExample() {
  const names = shuffle(MEMBER_NAMES).slice(0, 12);
  const short = names.slice(0, 10).map((name, i) => ({
    id: i + 1, name, length: (Math.random() * 17 + 3).toFixed(2), qty: "1",
  }));
  const long = names.slice(10, 12).map((name, i) => ({
    id: 11 + i, name, length: (Math.random() * 30 + 40).toFixed(2), qty: "1",
  }));
  const members = [...short, ...long];
  const kerf = 0.125;
  const stockLen = 96;
  // calculate total length needed including kerf between cuts
  const totalNeeded = members.reduce((s, m) => s + parseFloat(m.length), 0) + (members.length - 1) * kerf;
  // how many 96" pieces do we need (add 1 buffer for bin-packing inefficiency)
  const count = Math.ceil(totalNeeded / stockLen) + 1;
  // Use two different lengths with qty instead of listing each individually
  const stockPieces = [
    { id: 1, length: "96", qty: String(Math.ceil(count * 0.6)) },
    { id: 2, length: "72", qty: String(Math.ceil(count * 0.6)) },
  ];
  return {
    stockPieces, stockUnit: "in", kerf: "0.125",
    members, memberUnit: "in",
  };
}

export default function KerfCalculator() {
  const [stockPieces, setStockPieces] = useState([{ id: 1, length: "", qty: "1" }]);
  const [stockUnit, setStockUnit] = useState("in");
  const [kerf, setKerf] = useState("0.125");
  const [members, setMembers] = useState([{ id: 1, name: "", length: "", qty: "1" }]);
  const [memberUnit, setMemberUnit] = useState("in");
  const [result, setResult] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkInput, setBulkInput] = useState("");
  const projectInputRef = useRef(null);
  const csvInputRef = useRef(null);

  const addStock = () => { setStockPieces(s => [...s, { id: Date.now(), length: "", qty: "1" }]); setResult(null); };
  const removeStock = (id) => { setStockPieces(s => s.filter(p => p.id !== id)); setResult(null); };
  const updateStock = (id, val) => { setStockPieces(s => s.map(p => p.id === id ? { ...p, length: val } : p)); setResult(null); };
  const updateStockQty = (id, val) => { setStockPieces(s => s.map(p => p.id === id ? { ...p, qty: val } : p)); setResult(null); };

  const addMember = () => { setMembers(m => [...m, { id: Date.now(), name: "", length: "", qty: "1" }]); setResult(null); };
  const removeMember = (id) => { setMembers(m => m.filter(p => p.id !== id)); setResult(null); };
  const updateMember = (id, field, val) => { setMembers(m => m.map(p => p.id === id ? { ...p, [field]: val } : p)); setResult(null); };

  const handleBulkAdd = () => {
    const vals = bulkInput.split(/[\s,;]+/).map(s => s.trim()).filter(s => s && !isNaN(parseFloat(s)));
    if (vals.length) { setMembers(m => [...m, ...vals.map(v => ({ id: Date.now() + Math.random(), name: "", length: v, qty: "1" }))]); setBulkInput(""); setShowBulk(false); setResult(null); }
  };

  const loadExample = () => {
    const ex = generateExample();
    setStockPieces(ex.stockPieces); setStockUnit(ex.stockUnit);
    setMembers(ex.members); setMemberUnit(ex.memberUnit);
    setKerf(ex.kerf); setResult(null);
  };

  const runOptimize = () => {
    const kerfIn = Math.max(0, toInches(kerf, stockUnit) || 0);
    const stockIn = [];
    stockPieces.forEach(sp => {
      const lenIn = toInches(sp.length, stockUnit);
      const qty = Math.max(1, parseInt(sp.qty) || 1);
      if (!isNaN(lenIn) && lenIn > 0) {
        for (let q = 0; q < qty; q++) {
          stockIn.push({ id: sp.id, lenIn });
        }
      }
    });
    if (!stockIn.length) return;
    const membersIn = members.map((m, i) => ({
      id: i + 1, name: m.name.trim() || `#${i + 1}`,
      lenIn: toInches(m.length, memberUnit), qty: m.qty || "1",
    }));
    setResult(optimizeCuts(stockIn, membersIn, kerfIn));
  };

  const saveProject = () => {
    try {
      const data = JSON.stringify({ stockPieces, stockUnit, kerf, members, memberUnit }, null, 2);
      const blob = new Blob([data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "kerf-project.json";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch(e) { alert("Save failed: " + e.message); }
  };
  const loadProject = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const d = JSON.parse(ev.target.result);
        if (d.stockPieces) setStockPieces(d.stockPieces);
        if (d.stockUnit) setStockUnit(d.stockUnit);
        if (d.kerf !== undefined) setKerf(d.kerf);
        if (d.members) setMembers(d.members);
        if (d.memberUnit) setMemberUnit(d.memberUnit);
        setResult(null);
      } catch { alert("Invalid project file."); }
    };
    reader.readAsText(file); e.target.value = "";
  };

  const downloadCSV = () => {
    try {
      const rows = ["name,length,qty", ...members.map(m => `"${m.name}",${m.length},${m.qty || 1}`)];
      const blob = new Blob([rows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "members.csv";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch(e) { alert("Download failed: " + e.message); }
  };
  const uploadCSV = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split("\n").map(l => l.trim()).filter(Boolean);
      const newM = [];
      for (const line of lines) {
        if (/^(name|"name")/i.test(line)) continue;
        const parts = line.split(",").map(p => p.replace(/^"|"$/g, "").trim());
        const name = parts[0] || ""; const length = parts[1] || ""; const qty = parts[2] || "1";
        if (!isNaN(parseFloat(length)) && parseFloat(length) > 0) newM.push({ id: Date.now() + Math.random(), name, length, qty });
      }
      if (newM.length) { setMembers(m => [...m, ...newM]); setResult(null); }
    };
    reader.readAsText(file); e.target.value = "";
  };

  const printCutSheet = () => {
    if (!result) return;
    const dS = (v) => fmt(fromInches(v, stockUnit));
    const dM = (v) => fmt(fromInches(v, memberUnit));
    const kIn = Math.max(0, toInches(kerf, stockUnit) || 0);
    const html = `<!DOCTYPE html><html><head><title>Cut Sheet — Kerf Calculator</title>
<style>body{font-family:Arial,sans-serif;padding:28px;color:#000;font-size:13px}
h1{font-size:22px;margin:0 0 4px}
.meta{color:#555;font-size:12px;margin-bottom:20px}
.stock{border:1px solid #bbb;margin-bottom:14px;page-break-inside:avoid}
.sh{background:#eee;padding:8px 12px;font-weight:bold;font-size:13px;border-bottom:1px solid #bbb}
table{width:100%;border-collapse:collapse}
th,td{padding:5px 10px;text-align:left;border-bottom:1px solid #e8e8e8;font-size:12px}
th{background:#f5f5f5;font-weight:bold}
.totals{margin-top:16px;font-size:12px;color:#333;border-top:1px solid #ccc;padding-top:10px}
.warn{color:#c00;margin-top:10px;font-size:12px}
@media print{body{padding:10px}.noprint{display:none}}</style>
</head><body>
<h1>Cut Sheet — Kerf Calculator</h1>
<div class="meta">Generated ${new Date().toLocaleDateString()} &nbsp;·&nbsp; Kerf: ${dS(kIn)} ${stockUnit} &nbsp;·&nbsp; Stock: ${stockUnit} &nbsp;·&nbsp; Members: ${memberUnit}</div>
${result.bins.map((bin, bi) => `<div class="stock"><div class="sh">Stock Piece #${bi+1} &nbsp;—&nbsp; ${dS(bin.stockLen)} ${stockUnit} &nbsp;|&nbsp; ${bin.items.length} cuts &nbsp;|&nbsp; ${dS(bin.remaining)} ${stockUnit} scrap</div>
<table><tr><th>#</th><th>Name</th><th>Length (${memberUnit})</th></tr>
${bin.items.map((item,ii)=>`<tr><td>${ii+1}</td><td>${item.name}</td><td>${dM(item.len)}</td></tr>`).join("")}
</table></div>`).join("")}
${result.unassigned.length ? `<div class="warn">⚠ Unassigned: ${result.unassigned.map(m=>`${m.name} (${dM(m.len)} ${memberUnit})`).join(", ")}</div>` : ""}
<div class="totals">
Total stock used: ${result.bins.length} piece(s) &nbsp;·&nbsp;
Total kerf lost: ${dS(result.bins.reduce((s,b)=>s+b.kerfUsed,0))} ${stockUnit} &nbsp;·&nbsp;
Total scrap: ${dS(result.bins.reduce((s,b)=>s+b.remaining,0))} ${stockUnit}
</div>
<script>window.onload=()=>window.print();</script>
</body></html>`;
    try {
      const w = window.open("", "_blank");
      if (!w) { alert("Pop-up blocked. Please allow pop-ups for kerfcalculator.com and try again."); return; }
      w.document.write(html); w.document.close();
    } catch(e) { alert("Print failed: " + e.message); }
  };

  const kerfIn = Math.max(0, toInches(kerf, stockUnit) || 0);
  const dStock = (v) => fmt(fromInches(v, stockUnit));
  const dMember = (v) => fmt(fromInches(v, memberUnit));
  const totalMemberLength = members.reduce((s, m) => s + (parseFloat(m.length) || 0) * (parseInt(m.qty) || 1), 0);

  const inp = { background: "#1a1a1a", border: "1px solid #3a3a3a", color: "#fff", padding: "7px 9px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", borderRadius: 3, width: "100%" };
  const ST = { fontSize: 11, color: "#d4751a", letterSpacing: "0.18em", marginBottom: 10, textTransform: "uppercase", fontWeight: "700", display: "block" };
  const btnGhost = { background: "transparent", border: "1px solid #333", color: "#888", padding: "4px 10px", cursor: "pointer", fontSize: 11, fontFamily: "inherit", borderRadius: 3, letterSpacing: "0.05em" };

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d0d", color: "#e8e8e8", fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      <style>{`
        .sbo-layout{display:grid;grid-template-columns:370px 1fr;min-height:calc(100vh - 105px)}
        .sbo-left{background:#141414;border-right:1px solid #2a2a2a;padding:18px;display:flex;flex-direction:column;gap:16px;overflow-y:auto;height:calc(100vh - 105px)}
        .sbo-right{padding:22px;overflow-y:auto}
        .sbo-cards{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:16px}
        .sbo-bmc{position:absolute;top:16px;left:18px}
        .sbo-topright{position:absolute;top:16px;right:18px;display:flex;gap:7px}
        @media(max-width:720px){
          .sbo-layout{grid-template-columns:1fr;min-height:unset}
          .sbo-left{border-right:none;border-bottom:1px solid #2a2a2a;max-height:unset;padding:14px}
          .sbo-right{padding:14px}
          .sbo-cards{grid-template-columns:repeat(2,1fr)}
          .sbo-bmc{position:static;display:block;text-align:center;margin:8px auto 0;width:fit-content}
          .sbo-topright{position:static;display:flex;justify-content:center;margin-top:8px}
        }
      `}</style>

      <input type="file" accept=".json" ref={projectInputRef} style={{ display:"none" }} onChange={loadProject} />
      <input type="file" accept=".csv" ref={csvInputRef} style={{ display:"none" }} onChange={uploadCSV} />

      {/* HEADER */}
      <div style={{ background:"#111", borderBottom:"3px solid #d4751a", padding:"16px 24px", textAlign:"center", position:"relative" }}>
        <a href="https://www.buymeacoffee.com/nickclement" target="_blank" rel="noreferrer" className="sbo-bmc"
          style={{ display:"inline-block", background:"transparent", color:"#d4751a", fontWeight:"600", fontSize:11, padding:"5px 11px", borderRadius:3, textDecoration:"none", letterSpacing:"0.06em", border:"1px solid #d4751a", opacity:0.7 }}
          onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.7"}>
          Buy me a Zyn
        </a>
        <div className="sbo-topright">
          <button onClick={saveProject} style={btnGhost} title="Download project as JSON file">↓ Save Project</button>
          <button onClick={()=>projectInputRef.current.click()} style={btnGhost} title="Load a previously saved project file">↑ Load Project</button>
        </div>
        <div style={{ fontSize:26, fontWeight:"800", letterSpacing:"0.1em", color:"#fff", textTransform:"uppercase" }}>
          Kerf Calculator
        </div>
        <div style={{ fontSize:13, color:"#777", marginTop:3, letterSpacing:"0.03em" }}>
          Minimize scrap. Account for every cut.
        </div>
      </div>

      <div className="sbo-layout">
        {/* LEFT */}
        <div className="sbo-left">

          {/* Stock Pieces */}
          <section>
            <span style={ST}>Stock Settings</span>
            {/* Column headers */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 52px 44px 26px", gap:5, marginBottom:4 }}>
              <span style={{ fontSize:10, color:"#555", letterSpacing:"0.06em" }}>LENGTH</span>
              <span style={{ fontSize:10, color:"#555", letterSpacing:"0.06em" }}>UNIT</span>
              <span style={{ fontSize:10, color:"#555", letterSpacing:"0.06em" }}>QTY</span>
              <span />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:5, marginBottom:7 }}>
              {stockPieces.map((sp, i) => (
                <div key={sp.id} style={{ display:"grid", gridTemplateColumns:"1fr 52px 44px 26px", gap:5, alignItems:"center" }}>
                  <TooltipWrap text="The full length of one uncut piece of raw stock">
                    <input type="number" value={sp.length} placeholder="e.g. 96"
                      onChange={e=>updateStock(sp.id, e.target.value)} style={inp} />
                  </TooltipWrap>
                  {i === 0 ? (
                    <select value={stockUnit} onChange={e=>{setStockUnit(e.target.value);setResult(null);}}
                      style={{ ...inp, color:"#ccc", padding:"7px 4px" }}>
                      <option value="in">in</option><option value="ft">ft</option>
                      <option value="mm">mm</option><option value="cm">cm</option>
                    </select>
                  ) : (
                    <div style={{ fontSize:11, color:"#555", textAlign:"center" }}>{stockUnit}</div>
                  )}
                  <input type="number" value={sp.qty} min="1" placeholder="1"
                    onChange={e=>updateStockQty(sp.id, e.target.value)}
                    style={{ ...inp, textAlign:"center", padding:"7px 4px" }} />
                  <button onClick={()=>removeStock(sp.id)} disabled={stockPieces.length===1}
                    style={{ ...btnGhost, width:26, height:30, padding:0, fontSize:15, display:"flex", alignItems:"center", justifyContent:"center", opacity:stockPieces.length===1?0.2:1 }}>×</button>
                </div>
              ))}
            </div>
            <button onClick={addStock} style={{ ...btnGhost, width:"100%", padding:"5px", borderStyle:"dashed", fontSize:11 }}>
              + Add stock length
            </button>
            <div style={{ marginTop:10 }}>
              <label style={{ fontSize:11, color:"#777", display:"block", marginBottom:4, fontWeight:"600" }}>SAW BLADE KERF ({stockUnit})</label>
              <TooltipWrap text={`Width of material your saw blade removes per cut. Typically 0.125" for a cutoff wheel, 0.063" for a bandsaw.`}>
                <input type="number" value={kerf} step="0.001" min="0"
                  onChange={e=>{setKerf(e.target.value);setResult(null);}} style={inp} />
              </TooltipWrap>
            </div>
          </section>

          {/* Members */}
          <section style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
              <span style={{ ...ST, marginBottom:0 }}>
                Members <span style={{ color:"#444" }}>({members.reduce((s,m)=>s+(parseInt(m.qty)||1),0)})</span>
              </span>
              <div style={{ display:"flex", gap:5 }}>
                <TooltipWrap text="Paste a list of lengths all at once, separated by commas">
                  <button onClick={()=>setShowBulk(b=>!b)} style={{ ...btnGhost, fontSize:10 }}>{showBulk?"CLOSE":"BULK"}</button>
                </TooltipWrap>
                <button onClick={()=>csvInputRef.current.click()} style={{ ...btnGhost, fontSize:10 }} title="Upload CSV (name, length, qty)">↑ CSV</button>
                <button onClick={downloadCSV} style={{ ...btnGhost, fontSize:10 }} title="Download members as CSV">↓ CSV</button>
              </div>
            </div>

            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <span style={{ fontSize:11, color:"#666" }}>Member unit:</span>
              <select value={memberUnit} onChange={e=>{setMemberUnit(e.target.value);setResult(null);}}
                style={{ ...inp, width:"auto", color:"#ccc", padding:"4px 7px", fontSize:12 }}>
                <option value="in">inches</option><option value="ft">feet</option>
                <option value="mm">mm</option><option value="cm">cm</option>
              </select>
              {stockUnit !== memberUnit && <span style={{ fontSize:10, color:"#d4751a" }}>⚡ auto-converted</span>}
            </div>

            {showBulk && (
              <div style={{ marginBottom:8 }}>
                <textarea value={bulkInput} onChange={e=>setBulkInput(e.target.value)}
                  placeholder={`Lengths in ${memberUnit}, comma separated`}
                  style={{ ...inp, resize:"vertical", minHeight:50, fontSize:12 }} />
                <button onClick={handleBulkAdd} style={{ marginTop:4, background:"#d4751a", border:"none", color:"#000", padding:"6px", cursor:"pointer", fontSize:11, fontFamily:"inherit", fontWeight:"bold", width:"100%", borderRadius:3 }}>
                  ADD ALL
                </button>
              </div>
            )}

            <div style={{ display:"grid", gridTemplateColumns:"8px 20px 1fr 72px 38px 26px", gap:4, marginBottom:3 }}>
              {["","","NAME","LEN","QTY",""].map((h,i)=>(
                <div key={i} style={{ fontSize:9, color:"#444", letterSpacing:"0.06em", paddingLeft:i===2?2:0 }}>{h}</div>
              ))}
            </div>

            <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", gap:4 }}>
              {members.map((m, i) => (
                <div key={m.id} style={{ display:"grid", gridTemplateColumns:"8px 20px 1fr 72px 38px 26px", gap:4, alignItems:"center" }}>
                  <div style={{ width:8, height:26, background:memberColor(i+1), borderRadius:2 }} />
                  <div style={{ fontSize:10, color:"#444", textAlign:"right" }}>{i+1}</div>
                  <input value={m.name} placeholder="Name" onChange={e=>updateMember(m.id,"name",e.target.value)}
                    style={{ ...inp, padding:"5px 6px", fontSize:12 }} />
                  <input type="number" value={m.length} placeholder="Len" onChange={e=>updateMember(m.id,"length",e.target.value)}
                    style={{ ...inp, padding:"5px 6px", fontSize:12 }} />
                  <input type="number" value={m.qty} min="1" placeholder="1" onChange={e=>updateMember(m.id,"qty",e.target.value)}
                    style={{ ...inp, padding:"5px 4px", fontSize:12, textAlign:"center" }} />
                  <button onClick={()=>removeMember(m.id)}
                    style={{ ...btnGhost, width:26, height:26, padding:0, fontSize:13, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
                </div>
              ))}
            </div>

            <TooltipWrap text="Add another piece you need to cut">
              <button onClick={addMember} style={{ ...btnGhost, marginTop:7, width:"100%", padding:"7px", borderStyle:"dashed", fontSize:12 }}>
                + Add Member
              </button>
            </TooltipWrap>
            <div style={{ marginTop:5, fontSize:11, color:"#555" }}>
              Total: <span style={{ color:"#999" }}>{fmt(totalMemberLength)} {memberUnit}</span>
            </div>
          </section>

          {/* Buttons */}
          <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
            <TooltipWrap text="Loads a sample cut list so you can see how the optimizer works">
              <button onClick={loadExample} style={{ ...btnGhost, width:"100%", padding:"9px", fontSize:11, fontWeight:"700", letterSpacing:"0.1em", textTransform:"uppercase" }}
                onMouseEnter={e=>e.currentTarget.style.borderColor="#d4751a"}
                onMouseLeave={e=>e.currentTarget.style.borderColor="#333"}>
                LOAD EXAMPLE
              </button>
            </TooltipWrap>
            <button onClick={runOptimize}
              style={{ background:"#d4751a", border:"none", color:"#000", padding:"13px", fontSize:14, fontWeight:"800", letterSpacing:"0.1em", cursor:"pointer", fontFamily:"inherit", textTransform:"uppercase", borderRadius:4 }}
              onMouseEnter={e=>e.target.style.background="#e8861f"}
              onMouseLeave={e=>e.target.style.background="#d4751a"}>
              ▶ OPTIMIZE CUTS
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <div className="sbo-right">
          {!result ? (
            <div style={{ height:"100%", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", color:"#252525", textAlign:"center" }}>
              <div style={{ fontSize:58, marginBottom:14 }}>⊡</div>
              <div style={{ fontSize:14, letterSpacing:"0.06em", color:"#444" }}>Configure your inputs and click Optimize</div>
            </div>
          ) : (() => {
            const totalKerfIn = result.bins.reduce((s,b)=>s+b.kerfUsed,0);
            const totalScrapIn = result.bins.reduce((s,b)=>s+b.remaining,0);
            const totalCutIn = result.bins.reduce((s,b)=>s+b.items.reduce((ss,it)=>ss+it.len,0),0);
            const totalStockIn = result.bins.reduce((s,b)=>s+b.stockLen,0);
            return (
              <div>
                {/* Summary */}
                <div style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:16, flexWrap:"wrap" }}>
                  <div className="sbo-cards" style={{ flex:1, marginBottom:0 }}>
                    {[
                      { label:"STOCK PIECES", val:result.bins.length, color:"#fff" },
                      { label:"MEMBERS CUT", val:result.bins.reduce((s,b)=>s+b.items.length,0), color:"#fff" },
                      { label:"KERF LOST", val:`${dStock(totalKerfIn)} ${stockUnit}`, color:"#888" },
                      { label:"TOTAL SCRAP", val:`${dStock(totalScrapIn)} ${stockUnit}`, color:"#ff5555" },
                    ].map((s,i)=>(
                      <div key={i} style={{ background:"#1a1a1a", border:`1px solid ${i===3?"#4a1010":"#282828"}`, padding:"11px 13px", borderRadius:4 }}>
                        <div style={{ fontSize:9, color:i===3?"#c04040":"#666", letterSpacing:"0.12em", marginBottom:5, fontWeight:"700" }}>{s.label}</div>
                        <div style={{ fontSize:20, color:s.color, fontWeight:"800" }}>{s.val}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={printCutSheet}
                    style={{ background:"transparent", border:"1px solid #d4751a", color:"#d4751a", padding:"8px 14px", cursor:"pointer", fontSize:12, fontFamily:"inherit", borderRadius:4, fontWeight:"600", letterSpacing:"0.04em", whiteSpace:"nowrap", flexShrink:0 }}>
                    🖨 Print Cut Sheet
                  </button>
                </div>

                {/* Legend */}
                <div style={{ display:"flex", gap:14, alignItems:"center", marginBottom:14, padding:"7px 12px", background:"#161616", border:"1px solid #1e1e1e", borderRadius:4 }}>
                  <span style={{ fontSize:10, color:"#444", fontWeight:"700" }}>LEGEND</span>
                  {[
                    { bg:memberColor(1), label:"Cut member" },
                    { bg:"repeating-linear-gradient(45deg,#2a2a2a 0,#2a2a2a 2px,#111 2px,#111 5px)", border:"1px solid #444", label:"Saw kerf" },
                    { bg:"#3a0808", border:"1px solid #7a1010", label:"Scrap" },
                  ].map((l,i)=>(
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:16, height:11, background:l.bg, border:l.border, borderRadius:2, flexShrink:0 }} />
                      <span style={{ fontSize:12, color:"#aaa" }}>{l.label}</span>
                    </div>
                  ))}
                </div>

                {result.unassigned.length > 0 && (
                  <div style={{ background:"#1a0800", border:"1px solid #7a2000", padding:"9px 13px", marginBottom:14, fontSize:12, color:"#ff7040", borderRadius:4 }}>
                    ⚠ Could not assign: {result.unassigned.map(m=>`${m.name} (${dMember(m.len)} ${memberUnit}) — ${m.reason==="oversized"?"exceeds all stock":"insufficient stock"}`).join(", ")}
                  </div>
                )}

                {/* Stock cards */}
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {result.bins.map((bin, bi) => {
                    const usedCutIn = bin.items.reduce((s,it)=>s+it.len,0);
                    const yieldPct = ((usedCutIn/bin.stockLen)*100).toFixed(1);
                    return (
                      <div key={bi} style={{ background:"#181818", border:"1px solid #252525", borderRadius:5, overflow:"hidden" }}>
                        {/* Header */}
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"9px 15px", borderBottom:"1px solid #202020", background:"#1c1c1c" }}>
                          <div style={{ fontSize:13, color:"#d4751a", fontWeight:"800" }}>
                            STOCK PIECE #{bi+1} — {dStock(bin.stockLen)} {stockUnit}
                          </div>
                          <div style={{ display:"flex", gap:10, fontSize:11, color:"#666", flexWrap:"wrap", justifyContent:"flex-end" }}>
                            <span>{bin.items.length} cut{bin.items.length!==1?"s":""}</span>
                            <span style={{ color:"#aaa" }}>{dStock(usedCutIn)} used</span>
                            {bin.kerfUsed>0 && <span>{dStock(bin.kerfUsed)} kerf</span>}
                            <span style={{ color:bin.remaining>0.01?"#ff5555":"#883333", fontWeight:"700" }}>{dStock(bin.remaining)} {stockUnit} scrap</span>
                            <span style={{ color:parseFloat(yieldPct)>90?"#5fbf5f":parseFloat(yieldPct)>75?"#ccc":"#d08050" }}>{yieldPct}%</span>
                          </div>
                        </div>

                        {/* LINE 1: Visual bar */}
                        <div style={{ padding:"11px 15px 4px" }}>
                          <div style={{ height:34, background:"#0d0d0d", display:"flex", overflow:"hidden", border:"1px solid #1e1e1e", borderRadius:3 }}>
                            {bin.items.map((item,ii) => {
                              const ip = (item.len/bin.stockLen)*100;
                              const kp = ii<bin.items.length-1 ? (kerfIn/bin.stockLen)*100 : 0;
                              const label = `${item.name} — ${dMember(item.len)} ${memberUnit}`;
                              const shortLabel = `#${item.id} — ${dMember(item.len)}`;
                              return [
                                <div key={`m${ii}`} style={{ width:`${ip}%`, background:memberColor(item.id), display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"rgba(0,0,0,0.8)", fontWeight:"800", overflow:"hidden", flexShrink:0, borderRight:"1px solid rgba(0,0,0,0.2)", cursor:"default" }}
                                  title={label}>
                                  {ip>8 ? shortLabel : ip>4 ? `#${item.id}` : ""}
                                </div>,
                                kp>0 && <div key={`k${ii}`} style={{ width:`${Math.max(kp,0.3)}%`, flexShrink:0, backgroundImage:"repeating-linear-gradient(45deg,#2a2a2a 0,#2a2a2a 2px,#0d0d0d 2px,#0d0d0d 5px)", borderLeft:"1px solid #333", borderRight:"1px solid #333" }} title={`Kerf: ${dStock(kerfIn)} ${stockUnit}`} />,
                              ];
                            })}
                            {bin.remaining>1e-6 && (
                              <div style={{ flex:1, background:"#2e0606", backgroundImage:"repeating-linear-gradient(135deg,#3d0808 0,#3d0808 3px,#2e0606 3px,#2e0606 9px)", borderLeft:"1px dashed #7a1010", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:"#8a2020", fontWeight:"700" }}>
                                {(bin.remaining/bin.stockLen)>0.05?`SCRAP ${dStock(bin.remaining)} ${stockUnit}`:(bin.remaining/bin.stockLen)>0.02?"▓":""}
                              </div>
                            )}
                          </div>
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#333", marginTop:2, marginBottom:7 }}>
                            <span>0</span><span>{dStock(bin.stockLen*.25)}</span><span>{dStock(bin.stockLen*.5)}</span><span>{dStock(bin.stockLen*.75)}</span><span>{dStock(bin.stockLen)} {stockUnit}</span>
                          </div>
                        </div>

                        {/* LINE 2: Member chips + scrap chip */}
                        <div style={{ padding:"0 15px 11px", display:"flex", flexWrap:"wrap", gap:5 }}>
                          {bin.items.map(item => (
                            <div key={`${item.id}-${item.subIdx}`} style={{ display:"flex", alignItems:"center", gap:5, background:"#111", border:`1px solid ${memberColor(item.id)}35`, borderLeft:`3px solid ${memberColor(item.id)}`, padding:"4px 9px", borderRadius:"0 3px 3px 0" }}>
                              <span style={{ fontSize:12, color:"#ddd", fontWeight:"700" }}>{item.name}</span>
                              <span style={{ fontSize:11, color:"#666" }}>— {dMember(item.len)} {memberUnit}</span>
                            </div>
                          ))}
                          {bin.remaining>1e-6 && (
                            <div style={{ display:"flex", alignItems:"center", gap:5, background:"#1a0404", border:"1px solid #4a0e0e", borderLeft:"3px solid #aa2020", padding:"4px 9px", borderRadius:"0 3px 3px 0" }}>
                              <span style={{ fontSize:12, color:"#ff5555", fontWeight:"700" }}>scrap</span>
                              <span style={{ fontSize:11, color:"#aa3333" }}>— {dStock(bin.remaining)} {stockUnit}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary table */}
                <div style={{ marginTop:18, background:"#161616", border:"1px solid #202020", padding:"14px", borderRadius:5 }}>
                  <div style={{ fontSize:11, color:"#d4751a", letterSpacing:"0.15em", marginBottom:10, textTransform:"uppercase", fontWeight:"700" }}>Cut Sheet Summary</div>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                    <thead>
                      <tr style={{ borderBottom:"2px solid #252525" }}>
                        {["Stock #",`Length (${stockUnit})`,"Members",`Kerf (${stockUnit})`,`Scrap (${stockUnit})`,"Yield"].map(h=>(
                          <th key={h} style={{ textAlign:"left", padding:"4px 10px", color:"#555", fontWeight:"700", fontSize:10, letterSpacing:"0.07em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.bins.map((bin,bi)=>{
                        const usedCutIn = bin.items.reduce((s,it)=>s+it.len,0);
                        const yld = ((usedCutIn/bin.stockLen)*100).toFixed(1);
                        return (
                          <tr key={bi} style={{ borderBottom:"1px solid #1c1c1c" }}>
                            <td style={{ padding:"6px 10px", color:"#d4751a", fontWeight:"800" }}>#{bi+1}</td>
                            <td style={{ padding:"6px 10px", color:"#bbb" }}>{dStock(bin.stockLen)}</td>
                            <td style={{ padding:"6px 10px" }}>
                              <div style={{ display:"flex", gap:3, flexWrap:"wrap", alignItems:"center" }}>
                                {bin.items.map(m=>(
                                  <span key={`${m.id}-${m.subIdx}`} style={{ background:memberColor(m.id), width:8, height:8, display:"inline-block", borderRadius:1, flexShrink:0 }} title={`${m.name} (${dMember(m.len)} ${memberUnit})`} />
                                ))}
                                <span style={{ color:"#888", marginLeft:3, fontSize:11 }}>{bin.items.map(m=>m.name).join(", ")}</span>
                              </div>
                            </td>
                            <td style={{ padding:"6px 10px", color:"#666" }}>{dStock(bin.kerfUsed)}</td>
                            <td style={{ padding:"6px 10px", color:bin.remaining>0.01?"#ff5555":"#773030", fontWeight:"700" }}>{dStock(bin.remaining)}</td>
                            <td style={{ padding:"6px 10px", color:parseFloat(yld)>90?"#5fbf5f":parseFloat(yld)>75?"#ddd":"#d08050", fontWeight:"700" }}>{yld}%</td>
                          </tr>
                        );
                      })}
                      <tr style={{ borderTop:"2px solid #252525" }}>
                        <td colSpan={3} style={{ padding:"6px 10px", color:"#444", fontSize:11 }}>TOTALS</td>
                        <td style={{ padding:"6px 10px", color:"#666", fontWeight:"800" }}>{dStock(totalKerfIn)}</td>
                        <td style={{ padding:"6px 10px", color:"#cc3333", fontWeight:"800" }}>{dStock(totalScrapIn)}</td>
                        <td style={{ padding:"6px 10px", color:"#ccc", fontWeight:"800" }}>{((totalCutIn/totalStockIn)*100).toFixed(1)}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
